import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  modesOfTransport,
  otherGroupsFormEstablishmentTypes,
  schoolFormEstablishmentTypes,
  visitDays,
} from '../registrationFormComponents/data';
const GroupEmail = ({ onSubmit, initialOptions = null, adminData }) => {
  const { t } = useTranslation();

  // Check admin permissions
  const hasSchoolPermission = adminData?.assignedRegistrationTypes?.includes('school');
  const hasOtherGroupPermission = adminData?.assignedRegistrationTypes?.includes('other-group');

  // Expanded states for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    registration: true,
    scolaire: true,
    autreGroupe: true,
    byDate: true,
    byProduct: true,
    picnic: true,
    handicap: true,
    modeOfTransport: true,
    emailConsent: true,
  });

  const [selectedOptions, setSelectedOptions] = useState(
    initialOptions || {
      establishmentTypes: [],
      otherGroups: [],
      dates: [],
      picnic: {
        oui: false,
        non: false,
      },
      handicap: {
        oui: false,
        non: false,
      },
      transportModes: [],
      emailConsent: null, // null = nothing selected, 'oui' = true, 'non' = false
    },
  );

  // Effect to update selections if initialOptions changes
  useEffect(() => {
    if (initialOptions) {
      setSelectedOptions(initialOptions);
    }
  }, [initialOptions]);

  // Reset selected options when permissions change
  useEffect(() => {
    const updatedOptions = { ...selectedOptions };

    // If no permission for school, clear school selections
    if (!hasSchoolPermission) {
      updatedOptions.establishmentTypes = [];
    }

    // If no permission for other groups, clear other group selections
    if (!hasOtherGroupPermission) {
      updatedOptions.otherGroups = [];
    }

    setSelectedOptions(updatedOptions);
    onSubmit(updatedOptions); // Pass updated options to parent component
  }, [hasSchoolPermission, hasOtherGroupPermission]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleCheckboxChange = (category, value) => {
    const updatedOptions = { ...selectedOptions };

    if (category === 'establishmentTypes') {
      // Only allow selection if admin has school permission
      if (hasSchoolPermission) {
        updatedOptions.establishmentTypes = selectedOptions.establishmentTypes.includes(value)
          ? selectedOptions.establishmentTypes.filter((item) => item !== value)
          : [...selectedOptions.establishmentTypes, value];
      }
    } else if (category === 'otherGroups') {
      // Only allow selection if admin has other-group permission
      if (hasOtherGroupPermission) {
        updatedOptions.otherGroups = selectedOptions.otherGroups.includes(value)
          ? selectedOptions.otherGroups.filter((item) => item !== value)
          : [...selectedOptions.otherGroups, value];
      }
    } else if (category === 'dates') {
      updatedOptions.dates = selectedOptions.dates.includes(value)
        ? selectedOptions.dates.filter((item) => item !== value)
        : [...selectedOptions.dates, value];
    } else if (category === 'picnic') {
      updatedOptions.picnic = {
        ...selectedOptions.picnic,
        [value]: !selectedOptions.picnic[value],
      };
    } else if (category === 'handicap') {
      updatedOptions.handicap = {
        ...selectedOptions.handicap,
        [value]: !selectedOptions.handicap[value],
      };
    } else if (category === 'emailConsent') {
      // Radio button behavior: selecting same option deselects it, selecting different option switches to it
      updatedOptions.emailConsent = selectedOptions.emailConsent === value ? null : value;
    } else if (category === 'transportModes') {
      updatedOptions.transportModes = selectedOptions.transportModes.includes(value)
        ? selectedOptions.transportModes.filter((item) => item !== value)
        : [...selectedOptions.transportModes, value];
    } else if (category === 'allEstablishmentTypes') {
      // Toggle all establishment types only if admin has school permission
      if (hasSchoolPermission) {
        if (
          schoolFormEstablishmentTypes.every((type) =>
            selectedOptions.establishmentTypes.includes(type.value),
          )
        ) {
          updatedOptions.establishmentTypes = [];
        } else {
          updatedOptions.establishmentTypes = schoolFormEstablishmentTypes.map(
            (type) => type.value,
          );
        }
      }
    } else if (category === 'allOtherGroups') {
      // Toggle all other groups only if admin has other-group permission
      if (hasOtherGroupPermission) {
        if (
          otherGroupsFormEstablishmentTypes.every((type) =>
            selectedOptions.otherGroups.includes(type.value),
          )
        ) {
          updatedOptions.otherGroups = [];
        } else {
          updatedOptions.otherGroups = otherGroupsFormEstablishmentTypes.map((type) => type.value);
        }
      }
    } else if (category === 'allDates') {
      // Toggle all dates
      if (visitDays.every((day) => selectedOptions.dates.includes(day.value))) {
        updatedOptions.dates = [];
      } else {
        updatedOptions.dates = visitDays.map((day) => day.value);
      }
    } else if (category === 'allTransportModes') {
      // Toggle all transport modes
      if (modesOfTransport.every((mode) => selectedOptions.transportModes.includes(mode.value))) {
        updatedOptions.transportModes = [];
      } else {
        updatedOptions.transportModes = modesOfTransport.map((mode) => mode.value);
      }
    }

    setSelectedOptions(updatedOptions);
    onSubmit(updatedOptions); // Pass updated options to parent component
  };

  // Check if a group has some items selected (but not all)
  const getSelectionState = (items, selectedItems) => {
    if (!items || !selectedItems) return 'none';

    const selectedCount = items.filter((item) => selectedItems.includes(item.value)).length;

    if (selectedCount === 0) return 'none';
    if (selectedCount === items.length) return 'all';
    return 'some';
  };

  // Button indicator component with checkmark, dash, or empty
  const ButtonIndicator = ({ state = 'none', onClick }) => (
    <div
      className={`w-5 h-5 flex-shrink-0 ${state !== 'none' ? 'bg-teal-40' : 'bg-teal-30'} rounded-md cursor-pointer flex items-center justify-center`}
      onClick={(e) => {
        e.stopPropagation(); // Prevent triggering the section toggle
        if (onClick) onClick(e);
      }}>
      {state === 'all' && (
        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
      {state === 'some' && <div className="w-2 h-0.5 bg-white rounded-full"></div>}
    </div>
  );

  // Section header component
  const SectionHeader = ({ title, expanded, onClick, category }) => {
    let selectionState = 'none';

    if (category === 'registration') {
      const hasEstablishmentTypes = selectedOptions.establishmentTypes.length > 0;
      const hasOtherGroups = selectedOptions.otherGroups.length > 0;

      if (hasEstablishmentTypes || hasOtherGroups) {
        const allEstablishmentSelected =
          hasSchoolPermission &&
          schoolFormEstablishmentTypes.every((type) =>
            selectedOptions.establishmentTypes.includes(type.value),
          );
        const allOtherGroupsSelected =
          hasOtherGroupPermission &&
          otherGroupsFormEstablishmentTypes.every((type) =>
            selectedOptions.otherGroups.includes(type.value),
          );

        if (
          (hasSchoolPermission ? allEstablishmentSelected : true) &&
          (hasOtherGroupPermission ? allOtherGroupsSelected : true) &&
          (hasSchoolPermission || hasOtherGroupPermission)
        ) {
          selectionState = 'all';
        } else {
          selectionState = 'some';
        }
      }
    } else if (category === 'byDate') {
      if (selectedOptions.dates.length > 0) {
        selectionState = selectedOptions.dates.length === visitDays.length ? 'all' : 'some';
      }
    } else if (category === 'byProduct') {
      const hasPicnic = selectedOptions.picnic.oui || selectedOptions.picnic.non;
      const hasHandicap = selectedOptions.handicap.oui || selectedOptions.handicap.non;
      const hasTransport = selectedOptions.transportModes.length > 0;

      if (hasPicnic || hasHandicap || hasTransport) {
        // Complex logic for byProduct - if all subsections are fully selected
        const picnicFullySelected = selectedOptions.picnic.oui && selectedOptions.picnic.non;
        const handicapFullySelected = selectedOptions.handicap.oui && selectedOptions.handicap.non;
        const transportFullySelected =
          selectedOptions.transportModes.length === modesOfTransport.length;

        if (picnicFullySelected && handicapFullySelected && transportFullySelected) {
          selectionState = 'all';
        } else {
          selectionState = 'some';
        }
      }
    } else if (category === 'emailConsent') {
      // For emailConsent, show indicator based on selection state
      if (selectedOptions.emailConsent !== null) {
        selectionState = 'some'; // Always 'some' when one option is selected
      }
    }

    return (
      <button
        onClick={onClick}
        className="w-full flex items-start justify-between p-3 text-left font-medium gap-3">
        <div className="flex items-start flex-1 min-w-0">
          <svg
            className={`w-4 h-4 mr-2 mt-0.5 flex-shrink-0 transition-transform ${expanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span className="flex-1">{title}</span>
        </div>
        {/* Don't show ButtonIndicator for emailConsent category */}
        {category !== 'emailConsent' && (
          <ButtonIndicator
            state={selectionState}
            onClick={
              category === 'registration'
                ? () => {
                    if (selectionState === 'all') {
                      // Deselect all
                      const updatedOptions = { ...selectedOptions };
                      updatedOptions.establishmentTypes = [];
                      updatedOptions.otherGroups = [];
                      setSelectedOptions(updatedOptions);
                      onSubmit(updatedOptions);
                    } else {
                      // Select all based on permissions
                      const updatedOptions = { ...selectedOptions };
                      // Only add school types if admin has school permission
                      if (hasSchoolPermission) {
                        updatedOptions.establishmentTypes = schoolFormEstablishmentTypes.map(
                          (type) => type.value,
                        );
                      }
                      // Only add other group types if admin has other-group permission
                      if (hasOtherGroupPermission) {
                        updatedOptions.otherGroups = otherGroupsFormEstablishmentTypes.map(
                          (type) => type.value,
                        );
                      }
                      setSelectedOptions(updatedOptions);
                      onSubmit(updatedOptions);
                    }
                  }
                : category === 'byDate'
                  ? () => handleCheckboxChange('allDates')
                  : category === 'byProduct'
                    ? () => {
                        // Toggle all product options
                        const updatedOptions = { ...selectedOptions };
                        if (selectionState === 'all') {
                          // Deselect all
                          updatedOptions.picnic = { oui: false, non: false };
                          updatedOptions.handicap = { oui: false, non: false };
                          updatedOptions.transportModes = [];
                        } else {
                          // Select all
                          updatedOptions.picnic = { oui: true, non: true };
                          updatedOptions.handicap = { oui: true, non: true };
                          updatedOptions.transportModes = modesOfTransport.map(
                            (mode) => mode.value,
                          );
                        }
                        setSelectedOptions(updatedOptions);
                        onSubmit(updatedOptions);
                      }
                    : undefined
            }
          />
        )}
      </button>
    );
  };

  // Subsection header component
  const SubsectionHeader = ({ title, expanded, onClick, indentLevel = 1, category, items }) => {
    const paddingClass = indentLevel === 1 ? 'pl-8' : 'pl-16';
    const leftPosition = indentLevel === 1 ? 'left-8' : 'left-16';

    let selectionState = 'none';

    if (category === 'establishmentTypes') {
      const selectedItems = items.filter((item) =>
        selectedOptions.establishmentTypes.includes(item.value),
      );
      if (selectedItems.length === items.length) selectionState = 'all';
      else if (selectedItems.length > 0) selectionState = 'some';
    } else if (category === 'otherGroups') {
      const selectedItems = items.filter((item) =>
        selectedOptions.otherGroups.includes(item.value),
      );
      if (selectedItems.length === items.length) selectionState = 'all';
      else if (selectedItems.length > 0) selectionState = 'some';
    } else if (category === 'picnic') {
      if (selectedOptions.picnic.oui && selectedOptions.picnic.non) selectionState = 'all';
      else if (selectedOptions.picnic.oui || selectedOptions.picnic.non) selectionState = 'some';
    } else if (category === 'handicap') {
      if (selectedOptions.handicap.oui && selectedOptions.handicap.non) selectionState = 'all';
      else if (selectedOptions.handicap.oui || selectedOptions.handicap.non)
        selectionState = 'some';
    } else if (category === 'emailConsent') {
      // For emailConsent, show state based on which one is selected
      if (selectedOptions.emailConsent !== null) {
        selectionState = 'some';
      }
    } else if (category === 'transportModes') {
      if (selectedOptions.transportModes.length === modesOfTransport.length) selectionState = 'all';
      else if (selectedOptions.transportModes.length > 0) selectionState = 'some';
    }

    return (
      <div className="relative">
        <div className={`absolute ${leftPosition} right-3 border-b border-gray-40`}></div>
        <button
          onClick={onClick}
          className={`w-full flex items-center justify-between p-3 ${paddingClass} text-left relative z-10`}>
          <div className="flex items-center">
            <svg
              className={`w-4 h-4 mr-2 transition-transform ${expanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
            <span>{title}</span>
          </div>
          <ButtonIndicator
            state={selectionState}
            onClick={() => {
              if (category === 'establishmentTypes') {
                // Toggle all items in this establishment type
                const itemValues = items.map((item) => item.value);
                const updatedOptions = { ...selectedOptions };
                if (selectionState === 'all') {
                  // Remove all
                  updatedOptions.establishmentTypes = selectedOptions.establishmentTypes.filter(
                    (val) => !itemValues.includes(val),
                  );
                } else {
                  // Add all that aren't already added
                  const currentValues = new Set(selectedOptions.establishmentTypes);
                  itemValues.forEach((val) => currentValues.add(val));
                  updatedOptions.establishmentTypes = Array.from(currentValues);
                }
                setSelectedOptions(updatedOptions);
                onSubmit(updatedOptions);
              } else if (category === 'otherGroups') {
                // Toggle all items in other groups
                const itemValues = items.map((item) => item.value);
                const updatedOptions = { ...selectedOptions };
                if (selectionState === 'all') {
                  // Remove all
                  updatedOptions.otherGroups = selectedOptions.otherGroups.filter(
                    (val) => !itemValues.includes(val),
                  );
                } else {
                  // Add all that aren't already added
                  const currentValues = new Set(selectedOptions.otherGroups);
                  itemValues.forEach((val) => currentValues.add(val));
                  updatedOptions.otherGroups = Array.from(currentValues);
                }
                setSelectedOptions(updatedOptions);
                onSubmit(updatedOptions);
              } else if (category === 'picnic') {
                // Toggle both oui and non
                const updatedOptions = { ...selectedOptions };
                updatedOptions.picnic = {
                  oui: selectionState !== 'all',
                  non: selectionState !== 'all',
                };
                setSelectedOptions(updatedOptions);
                onSubmit(updatedOptions);
              } else if (category === 'handicap') {
                // Toggle both oui and non
                const updatedOptions = { ...selectedOptions };
                updatedOptions.handicap = {
                  oui: selectionState !== 'all',
                  non: selectionState !== 'all',
                };
                setSelectedOptions(updatedOptions);
                onSubmit(updatedOptions);
              } else if (category === 'emailConsent') {
                // Clear selection for emailConsent
                const updatedOptions = { ...selectedOptions };
                updatedOptions.emailConsent = null;
                setSelectedOptions(updatedOptions);
                onSubmit(updatedOptions);
              } else if (category === 'transportModes') {
                // Toggle all transport modes
                const updatedOptions = { ...selectedOptions };
                if (selectionState === 'all') {
                  updatedOptions.transportModes = [];
                } else {
                  updatedOptions.transportModes = modesOfTransport.map((mode) => mode.value);
                }
                setSelectedOptions(updatedOptions);
                onSubmit(updatedOptions);
              }
            }}
          />
        </button>
        {expanded && (
          <div className="relative">
            <div className={`absolute ${leftPosition} right-3 border-b border-gray-40`}></div>
          </div>
        )}
      </div>
    );
  };

  // Option item component
  const OptionItem = ({ label, indentLevel = 2, category, value }) => {
    const paddingClass = indentLevel === 2 ? 'pl-16' : 'pl-8';

    let isChecked = false;

    if (category === 'establishmentTypes') {
      isChecked = selectedOptions.establishmentTypes.includes(value);
    } else if (category === 'otherGroups') {
      isChecked = selectedOptions.otherGroups.includes(value);
    } else if (category === 'transportModes') {
      isChecked = selectedOptions.transportModes.includes(value);
    } else if (category === 'picnic') {
      isChecked = selectedOptions.picnic[value];
    } else if (category === 'handicap') {
      isChecked = selectedOptions.handicap[value];
    } else if (category === 'emailConsent') {
      isChecked = selectedOptions.emailConsent === value;
    }

    return (
      <div className={`flex items-center justify-between p-3 ${paddingClass}`}>
        <span>{label}</span>
        <ButtonIndicator
          state={isChecked ? 'all' : 'none'}
          onClick={() => handleCheckboxChange(category, value)}
        />
      </div>
    );
  };

  // Date item component with custom separator
  const DateItem = ({ label, value }) => {
    const isChecked = selectedOptions.dates.includes(value);

    return (
      <div className="relative">
        <div className="absolute left-8 right-3 border-b border-gray-40"></div>
        <div className="flex items-center justify-between p-3 pl-8 relative z-10">
          <span>{label}</span>
          <ButtonIndicator
            state={isChecked ? 'all' : 'none'}
            onClick={() => handleCheckboxChange('dates', value)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col space-y-4 bg-teal-10/80 p-4 rounded-lg">
      {/* Show a message if admin has no groups assigned */}
      {!hasSchoolPermission && !hasOtherGroupPermission ? (
        <div className="bg-white rounded-xl overflow-hidden shadow-sm p-4 text-center">
          <p className="text-gray-600">You don't have any group permissions assigned.</p>
          <p className="text-gray-500 text-sm mt-2">
            Contact an administrator if you believe this is an error.
          </p>
        </div>
      ) : (
        <>
          {/* Registration Groups / Establishment Types */}
          <div className="bg-white border border-teal-10 rounded-xl overflow-hidden shadow-sm">
            <SectionHeader
              title={t('groupEmail.filters.byRegistration')}
              expanded={expandedSections.registration}
              onClick={() => toggleSection('registration')}
              category="registration"
            />

            {expandedSections.registration && (
              <div>
                {/* Scolaire subsection - Only show if admin has school permission */}
                {hasSchoolPermission && (
                  <div>
                    <SubsectionHeader
                      title="Scolaire"
                      expanded={expandedSections.scolaire}
                      onClick={() => toggleSection('scolaire')}
                      category="establishmentTypes"
                      items={schoolFormEstablishmentTypes}
                    />

                    {expandedSections.scolaire && (
                      <div className="pb-2">
                        {schoolFormEstablishmentTypes.map((type) => (
                          <OptionItem
                            key={type.value}
                            label={type.label}
                            category="establishmentTypes"
                            value={type.value}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Autre groupe subsection - Only show if admin has other-group permission */}
                {hasOtherGroupPermission && (
                  <div>
                    <SubsectionHeader
                      title="Autre groupe"
                      expanded={expandedSections.autreGroupe}
                      onClick={() => toggleSection('autreGroupe')}
                      category="otherGroups"
                      items={otherGroupsFormEstablishmentTypes}
                    />

                    {expandedSections.autreGroupe && (
                      <div className="pb-2">
                        {otherGroupsFormEstablishmentTypes.map((type) => (
                          <OptionItem
                            key={type.value}
                            label={type.label}
                            category="otherGroups"
                            value={type.value}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* By Date */}
          <div className="bg-white border border-teal-10 rounded-xl overflow-hidden shadow-sm">
            <SectionHeader
              title={t('groupEmail.filters.byDate')}
              expanded={expandedSections.byDate}
              onClick={() => toggleSection('byDate')}
              category="byDate"
            />

            {expandedSections.byDate && (
              <div>
                {visitDays.map((day) => (
                  <DateItem key={day.value} label={day.label} value={day.value} />
                ))}
              </div>
            )}
          </div>

          {/* By Product */}
          <div className="bg-white border border-teal-10 rounded-xl overflow-hidden shadow-sm">
            <SectionHeader
              title={t('groupEmail.filters.byProduct')}
              expanded={expandedSections.byProduct}
              onClick={() => toggleSection('byProduct')}
              category="byProduct"
            />

            {expandedSections.byProduct && (
              <div>
                {/* Picnic subsection */}
                <div>
                  <SubsectionHeader
                    title={t('groupEmail.filters.byPicnic')}
                    expanded={expandedSections.picnic}
                    onClick={() => toggleSection('picnic')}
                    category="picnic"
                  />

                  {expandedSections.picnic && (
                    <div className="pb-2">
                      <div className="flex items-center justify-between p-3 pl-16">
                        <span>Oui</span>
                        <ButtonIndicator
                          state={selectedOptions.picnic.oui ? 'all' : 'none'}
                          onClick={() => handleCheckboxChange('picnic', 'oui')}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 pl-16">
                        <span>Non</span>
                        <ButtonIndicator
                          state={selectedOptions.picnic.non ? 'all' : 'none'}
                          onClick={() => handleCheckboxChange('picnic', 'non')}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Guide on-site subsection */}
                <div>
                  <SubsectionHeader
                    title={t('groupEmail.filters.byHandicap')}
                    expanded={expandedSections.handicap}
                    onClick={() => toggleSection('handicap')}
                    category="handicap"
                  />

                  {expandedSections.handicap && (
                    <div className="pb-2">
                      <div className="flex items-center justify-between p-3 pl-16">
                        <span>Oui</span>
                        <ButtonIndicator
                          state={selectedOptions.handicap.oui ? 'all' : 'none'}
                          onClick={() => handleCheckboxChange('handicap', 'oui')}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 pl-16">
                        <span>Non</span>
                        <ButtonIndicator
                          state={selectedOptions.handicap.non ? 'all' : 'none'}
                          onClick={() => handleCheckboxChange('handicap', 'non')}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Mode of Transport subsection */}
                <div>
                  <SubsectionHeader
                    title={t('groupEmail.filters.byModeOfTransport')}
                    expanded={expandedSections.modeOfTransport}
                    onClick={() => toggleSection('modeOfTransport')}
                    category="transportModes"
                  />

                  {expandedSections.modeOfTransport && (
                    <div className="pb-2">
                      {modesOfTransport.map((mode) => (
                        <OptionItem
                          key={mode.value}
                          label={mode.label}
                          category="transportModes"
                          value={mode.value}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Email Consent for Satisfaction Survey */}
          <div className="bg-white border border-teal-10 rounded-xl overflow-hidden shadow-sm">
            <SectionHeader
              title={t('groupEmail.filters.byEmailConsent')}
              expanded={expandedSections.emailConsent}
              onClick={() => toggleSection('emailConsent')}
              category="emailConsent"
            />

            {expandedSections.emailConsent && (
              <div className="pb-2">
                <div className="relative">
                  <div className="absolute left-8 right-3 border-b border-gray-40"></div>
                  <div className="flex items-center justify-between p-3 pl-8 relative z-10">
                    <span>Non</span>
                    <ButtonIndicator
                      state={selectedOptions.emailConsent === 'non' ? 'all' : 'none'}
                      onClick={() => handleCheckboxChange('emailConsent', 'non')}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 pl-8">
                  <span>Oui</span>
                  <ButtonIndicator
                    state={selectedOptions.emailConsent === 'oui' ? 'all' : 'none'}
                    onClick={() => handleCheckboxChange('emailConsent', 'oui')}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GroupEmail;
